export interface TranslationRequest {
  key: string;
  text: string;
}

export interface TranslationResult {
  key: string;
  original: string;
  translated: string;
}

export interface TranslationServiceConfig {
  apiKey: string;
  targetLanguage?: string;
  chunkSize?: number;
}

export class TranslationService {
  private cache: Map<string, string> = new Map();
  private config: TranslationServiceConfig;
  private readonly defaultChunkSize = 120;

  constructor(config: TranslationServiceConfig) {
    this.config = {
      targetLanguage: 'ja',
      chunkSize: this.defaultChunkSize,
      ...config,
    };
  }

  getCached(text: string): string | undefined {
    return this.cache.get(text);
  }

  async translate(requests: TranslationRequest[]): Promise<TranslationResult[]> {
    if (requests.length === 0) {
      return [];
    }

    const results: TranslationResult[] = [];
    const uncachedRequests: TranslationRequest[] = [];

    for (const request of requests) {
      const cached = this.getCached(request.text);
      if (cached) {
        results.push({
          key: request.key,
          original: request.text,
          translated: cached,
        });
      } else {
        uncachedRequests.push(request);
      }
    }

    if (uncachedRequests.length > 0) {
      const apiResults = await this.translateViaApi(uncachedRequests);
      results.push(...apiResults);
    }

    return results;
  }

  private async translateViaApi(requests: TranslationRequest[]): Promise<TranslationResult[]> {
    const chunkSize = this.config.chunkSize || this.defaultChunkSize;
    const chunks: TranslationRequest[][] = [];

    for (let i = 0; i < requests.length; i += chunkSize) {
      chunks.push(requests.slice(i, i + chunkSize));
    }

    const chunkPromises = chunks.map(chunk => this.translateChunk(chunk));
    const chunkResults = await Promise.all(chunkPromises);

    return chunkResults.flat();
  }

  private async translateChunk(requests: TranslationRequest[]): Promise<TranslationResult[]> {
    const texts = requests.map(r => r.text);
    
    try {
      const response = await fetch(
        `https://translation.googleapis.com/language/translate/v2?key=${this.config.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            q: texts,
            target: this.config.targetLanguage,
            format: 'text',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Translation API error: ${response.status}`);
      }

      const data = await response.json();
      const translations = data.data.translations;

      return requests.map((request, index) => {
        const translatedText = this.decodeHtmlEntities(translations[index].translatedText);
        this.cache.set(request.text, translatedText);

        return {
          key: request.key,
          original: request.text,
          translated: translatedText,
        };
      });
    } catch (error) {
      console.error('Translation error:', error);
      return requests.map(request => ({
        key: request.key,
        original: request.text,
        translated: request.text,
      }));
    }
  }

  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  clearCache(): void {
    this.cache.clear();
  }

  getCacheSize(): number {
    return this.cache.size;
  }
}
